<template>
  <div class="card border-0 shadow-sm m-4">
    <div class="card-body p-0 gap-4 d-flex flex-column">
      <div class="transaction-list-section p-0">
        <div class="transaction-list-table">
          <!-- Skeleton Loader -->
          <div v-if="loading || isLoading" class="skeleton-container">
            <!-- Header Controls Skeleton -->
            <div
              class="transaction-list-table-header d-flex align-items-center w-100 flex-wrap px-4 pt-4"
              style="gap: 8px"
            >
              <div class="flex-grow-1">
                <div class="row g-2 align-items-center">
                  <div class="col-auto">
                    <div class="skeleton-input skeleton-select"></div>
                  </div>
                  <div class="col-auto">
                    <div class="skeleton-input skeleton-date"></div>
                  </div>
                </div>
              </div>
              <div class="d-flex align-items-center ms-auto pe-0">
                <div class="skeleton-button skeleton-export"></div>
              </div>
            </div>
            
            <hr />
            
            <!-- Table Skeleton -->
            <div class="mt-4 px-4">
              <div class="skeleton-table">
                <!-- Table Header Skeleton -->
                <div class="skeleton-table-header">
                  <div class="skeleton-cell skeleton-cell-header" v-for="i in 9" :key="i"></div>
                </div>
                <!-- Table Rows Skeleton -->
                <div class="skeleton-table-row" v-for="i in 5" :key="i">
                  <div class="skeleton-cell" v-for="j in 9" :key="j"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actual Content -->
          <div v-else>
            <div
              class="transaction-list-table-header d-flex align-items-center w-100 flex-wrap px-4 pt-4"
              style="gap: 8px"
            >
              <div class="flex-grow-1">
                <div class="row g-2 align-items-center">
                  <div class="col-auto">
                    <div class="input-wrapper search-input-wrapper">
                      <span class="input-icon icon-left">
                        <i class="fa fa-search"></i>
                      </span>
                      <input
                        type="text"
                        placeholder="Search Transaction ID"
                        v-model="search"
                        class="search-input styled-input"
                      />
                    </div>
                  </div>
                  <div class="col-auto">
                    <div class="input-wrapper status-input-wrapper">
                      <select v-model="status" class="status-select styled-input">
                        <option value="">All Status</option>
                        <option
                          v-for="option in statusOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.text }}
                        </option>
                      </select>
                      <span class="input-icon icon-right select-arrow">
                        <i class="fa fa-chevron-down"></i>
                      </span>
                    </div>
                  </div>
                  <div class="col-auto">
                    <div class="searchable-select-wrapper" v-click-outside="closeBankDropdown">
                      <div class="input-wrapper status-input-wrapper">
                        <input
                          type="text"
                          v-model="bankSearch"
                          @focus="showBankDropdown = true"
                          @input="showBankDropdown = true"
                          :placeholder="selectedBankName || 'All Banks'"
                          class="status-select styled-input searchable-input"
                          autocomplete="off"
                        />
                        <span class="input-icon icon-right select-arrow" @click="toggleBankDropdown">
                          <i class="fa" :class="showBankDropdown ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </span>
                      </div>
                      <div v-if="showBankDropdown" class="searchable-dropdown">
                        <div 
                          class="dropdown-item" 
                          :class="{ 'selected': bankId === '' }"
                          @click="selectBank('')"
                        >
                          All Banks
                        </div>
                        <div 
                          v-for="option in filteredBankOptions"
                          :key="option.id"
                          class="dropdown-item"
                          :class="{ 'selected': bankId === option.id }"
                          @click="selectBank(option.id)"
                        >
                          {{ option.bank_name }}
                        </div>
                        <div v-if="filteredBankOptions.length === 0 && bankSearch" class="dropdown-item no-results">
                          No banks found
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-auto">
                    <SwiftDatePicker
                      v-model="dateRange"
                      placeholder="Select Date Range"
                    />
                  </div>
                </div>
              </div>
              <div
                class="d-flex align-items-center ms-auto pe-0 export-btn-responsive"
                style="min-width: 0"
              >
                <argon-button variant="export" @click="exportData()">
                  Export
                </argon-button>
              </div>
            </div>
            <hr />
            <div class="mt-4 table-wrapper" style="overflow-x: auto">
              <!-- Empty State -->
              <div v-if="!isLoading && transactions.length === 0" class="empty-state">
                <div class="empty-state-content">
                  <i class="fas fa-receipt fa-3x text-muted mb-3"></i>
                  <h5 class="text-muted">No Transactions Found</h5>
                  <p class="text-muted">There are no API transactions to display at the moment.</p>
                </div>
              </div>
              
              <!-- Table Content -->
              <table v-else class="table table-hover">
                <thead class="table-header-sticky">
                  <tr>
                    <th
                      scope="col"
                      v-for="(header, index) in tableHeaders"
                      :key="index"
                      :style="getHeaderStyle(header)"
                      :class="[
                        'table-header',
                        header.fixed === 'left' ? 'sticky-col-left' : '',
                        header.fixed === 'right' ? 'sticky-col-right' : '',
                      ]"
                    >
                      {{ header.text }}
                      <i v-if="header.sortable" class="fas fa-sort ms-1 text-muted"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, rowIndex) in filteredTransactions"
                    :key="row.transaction_id || rowIndex"
                    class="table-row-hover"
                    :class="{ 'row-processing': isResendingCallback === row.transaction_id }"
                  >
                    <td
                      v-for="(header, colIndex) in tableHeaders"
                      :key="colIndex"
                      :style="getHeaderStyle(header)"
                      :class="[
                        'table-body',
                        header.fixed === 'left' ? 'sticky-col-left' : '',
                        header.fixed === 'right' ? 'sticky-col-right' : '',
                      ]"
                      style="vertical-align: middle"
                    >
                      <template v-if="header.value === 'amount'">
                        IDR {{ formatNumber(row[header.value]) }}
                      </template>
                      <template v-else-if="header.value === 'admin_fee'">
                        IDR {{ formatNumber(row[header.value]) }}
                      </template>
                      <template v-else-if="header.value === 'transaction_id'">
                        <div class="transaction-id-cell">
                          <span 
                            class="transaction-id-text clickable" 
                            :title="row[header.value]"
                            @click="openTransactionDetail(row)"
                          >
                            {{ row[header.value] }}
                          </span>
                          <div class="transaction-actions">
                            <i 
                              class="fas fa-copy copy-icon" 
                              @click.stop="copyToClipboard(row[header.value], 'Transaction ID')"
                              title="Copy Transaction ID"
                            ></i>
                            <i 
                              class="fas fa-eye view-icon" 
                              @click.stop="openTransactionDetail(row)"
                              title="View Details"
                            ></i>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="header.value === 'uuid'">
                        <div 
                          v-if="row.is_refund" 
                          class="uuid-link" 
                          @click="navigateToBalance(row.uuid)"
                          title="Click to view in Balance page"
                        >
                          {{ row[header.value] }}
                          <i class="fas fa-external-link-alt ms-1"></i>
                        </div>
                        <div v-else style="color: #6c757d;">-</div>
                      </template>
                      <template v-else-if="header.value === 'status'">
                        <span
                          :class="[
                            'status-badge',
                            statusClass(row[header.value]),
                          ]"
                        >
                          {{ row[header.value] }}
                        </span>
                      </template>
                      <template v-else-if="header.value === 'action'">
                        <div class="action-buttons">
                          <button
                            @click="handleResendCallback(row)"
                            :disabled="isResendingCallback === row.transaction_id || isResendingCallback !== null"
                            :title="`Resend callback for transaction ${row.transaction_id}`"
                            class="resend-btn-custom"
                            :class="{ 'loading': isResendingCallback === row.transaction_id }"
                          >
                            <i v-if="isResendingCallback === row.transaction_id" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-redo"></i>
                          </button>
                        </div>
                      </template>
                      <template v-else-if="header.value === 'account_number'">
                        {{ maskAccountNumber(row[header.value], row.bank) }}
                      </template>
                      <template v-else-if="header.value === 'bank'">
                        <div class="bank-name-cell" :title="row[header.value]">
                          {{ row[header.value] }}
                        </div>
                      </template>
                      <template v-else>
                        {{ row[header.value] }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Pagination Controls -->
            <div class="pagination-controls d-flex align-items-center justify-content-between mt-4 p-4" style="border-top: 1px solid #eaecf0" v-if="!isLoading && transactions.length > 0">
              <div class="per-page-control d-flex align-items-center">
                <span class="me-2">Show:</span>
                <div class="select-with-icon position-relative" style="position: relative">
                  <select class="form-select form-select-sm" v-model="perPage" @change="onPerPageChange">
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                  </select>
                </div>
              </div>
              
              <div class="d-flex align-items-center">
                <span class="pagination-info me-3">
                  Showing {{ displayStart }} to {{ displayEnd }} of {{ totalFiltered }} entries
                </span>
                
                <nav aria-label="Transactions pagination">
                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: !hasPrevPage }">
                      <button class="page-link" @click="onPreviousPageFiltered" :disabled="!hasPrevPage">
                        <i class="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    <li class="page-item">
                      <span class="page-link bg-primary text-white">{{ currentPage }}</span>
                    </li>
                    <li class="page-item" :class="{ disabled: !hasNextPage }">
                      <button class="page-link" @click="onNextPageFiltered" :disabled="!hasNextPage">
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Loading :isLoading="isLoading && !loading && !isExporting" />
  
  <!-- Transaction Detail Modal -->
  <TransactionDetailModal
    :show="showTransactionModal"
    :transactionData="selectedTransaction"
    @close="closeTransactionModal"
  />
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import SwiftDatePicker from "@/components/SwiftDatePicker.vue";
import TransactionDetailModal from "@/components/TransactionDetailModal.vue";
import { getTransactionsList, resendCallback, getBankList } from "@/api/transaction";
import { formatNumber as formatNumberUtil, maskAccountNumber } from "@/utils/param-utils";
import Loading from "@/components/Loading";

export default {
  name: "ApiTransactionListComponent",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  },
  components: {
    ArgonButton,
    SwiftDatePicker,
    TransactionDetailModal,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      isExporting: false, // Track if export is in progress
      isResendingCallback: null, // Track which transaction is being resent
      searchTimeout: null, // For debouncing search
      dateRange: null,
      status: "",
      bankId: "",
      bankSearch: "",
      showBankDropdown: false,
      search: "",
      perPage: 10,
      currentPage: 1,
      transactions: [],
      totalItems: 0,
      showTransactionModal: false,
      selectedTransaction: null,
      tableHeaders: [
        {
          text: "Transaction ID",
          value: "transaction_id",
          fixed: "left",
          sortable: false,
          width: 280,
        },
        {
          text: "Date & Time",
          value: "transaction_datetime",
          sortable: false,
          width: 160,
        },
        { text: "Amount", value: "amount", sortable: false, width: 150 },
        { text: "Admin Fee", value: "admin_fee", sortable: false, width: 150 },
        {
          text: "Bank",
          value: "bank",
          sortable: false,
          width: 200,
        },
        {
          text: "Account Number",
          value: "account_number",
          sortable: false,
          width: 180,
        },
        {
          text: "Account Name",
          value: "account_name",
          sortable: false,
          width: 180,
        },
        {
          text: "Status",
          value: "status",
          sortable: false,
          width: 120,
        },
        {
          text: "Action",
          value: "action",
          sortable: false,
          width: 80,
          fixed: "right",
        },
      ],
      statusOptions: [
        { text: "Success", value: "success" },
        { text: "Failed", value: "refunded" },
        { text: "Process", value: "process" },
      ],
      bankOptions: [],
    };
  },
  computed: {
    // Check if any filters are active
    hasActiveFilters() {
      return !!(
        this.search ||
        this.status ||
        this.bankId ||
        (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1])
      );
    },

    // Get all filtered data (without pagination)
    allFilteredTransactions() {
      let filtered = this.transactions;

      // Filter by search (transaction_id)
      if (this.search) {
        filtered = filtered.filter((t) =>
          t.transaction_id && t.transaction_id.toLowerCase().includes(this.search.toLowerCase())
        );
      }

      // Filter by status if set
      if (this.status) {
        filtered = filtered.filter((item) => item.status && item.status.toLowerCase() === this.status.toLowerCase());
      }

      // Filter by bank if set (client-side fallback, but mainly handled by server)
      if (this.bankId) {
        filtered = filtered.filter((item) => item.bank_id && String(item.bank_id) === String(this.bankId));
      }

      // Filter by date range if set
      if (
        this.dateRange &&
        Array.isArray(this.dateRange) &&
        this.dateRange.length === 2 &&
        this.dateRange[0] &&
        this.dateRange[1]
      ) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);
        endDate.setHours(23, 59, 59, 999);

        filtered = filtered.filter((item) => {
          const itemDate = new Date(item.transaction_datetime);
          return itemDate >= startDate && itemDate <= endDate;
        });
      }

      return filtered;
    },

    // Get filtered data for current page display
    filteredTransactions() {
      // Always use server-side data (filters are applied on server)
      return this.transactions;
    },

    // Calculate display values for pagination info
    totalFiltered() {
      // Always use server pagination total (filters are applied on server)
      return this.totalItems;
    },
    
    displayStart() {
      if (this.totalFiltered === 0) return 0;
      return Math.min((this.currentPage - 1) * this.perPage + 1, this.totalFiltered);
    },
    
    displayEnd() {
      if (this.totalFiltered === 0) return 0;
      return Math.min(this.currentPage * this.perPage, this.totalFiltered);
    },
    
    // Calculate if there are previous/next pages
    hasPrevPage() {
      return this.currentPage > 1;
    },
    
    hasNextPage() {
      // Always use server pagination info (filters are applied on server)
      return this.currentPage < this.totalPages;
    },

    totalPages() {
      // Always use server pagination total
      return Math.ceil(this.totalItems / Number(this.perPage));
    },

    // Get filtered bank options based on search
    filteredBankOptions() {
      if (!this.bankSearch || this.bankSearch.trim() === '') {
        return this.bankOptions;
      }
      
      const searchLower = this.bankSearch.toLowerCase();
      return this.bankOptions.filter(bank => 
        bank.bank_name && bank.bank_name.toLowerCase().includes(searchLower)
      );
    },

    // Get selected bank name for placeholder
    selectedBankName() {
      if (!this.bankId) return '';
      const selected = this.bankOptions.find(b => b.id === this.bankId);
      return selected ? selected.bank_name : '';
    },
  },
  mounted() {
    this.fetchBanks();
    this.fetchTransactions();
  },
  methods: {
    statusClass(status) {
      switch (status.toLowerCase()) {
        case "success":
          return "success";
        case "failed":
        case "refunded":
          return "refunded";
        case "process":
        case "processing":
          return "process";
        case "pending":
          return "waiting";
        default:
          return "";
      }
    },
    async fetchBanks() {
      try {
        const response = await getBankList();
        
        if (response.status === "success" || response.data) {
          // Handle nested data structure from API
          this.bankOptions = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        } else {
          this.bankOptions = [];
          console.warn('Failed to fetch bank list:', response.message);
        }
      } catch (error) {
        console.error('Error fetching banks:', error);
        this.bankOptions = [];
        // Don't show error to user for bank list fetch failure, just log it
      }
    },
    async fetchTransactions() {
      try {
        this.isLoading = true;
        const params = {
          page: this.currentPage,
          per_page : this.perPage,
        };

        if (this.search && this.search.trim() !== "") {
          params.transaction_id = this.search.trim();
        }

        if (this.status && this.status.trim() !== "") {
          params.status = this.status.trim();
        }

        if (this.bankId && this.bankId !== "") {
          params.bank_id = this.bankId;
        }

        if (
          this.dateRange &&
          Array.isArray(this.dateRange) &&
          this.dateRange.length === 2 &&
          this.dateRange[0] &&
          this.dateRange[1]
        ) {
          params.start_date = this.formatDateToAPI(new Date(this.dateRange[0]));
          params.end_date = this.formatDateToAPI(new Date(this.dateRange[1]));
        }

        const response = await getTransactionsList(params);
        
        if (response.status === "success" || response.data) {
          // Handle nested data structure from API
          this.transactions = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          
          // Extract pagination info properly
          if (response.data?.pagination) {
            this.totalItems = response.data.pagination.total || 0;
            // We could also use these if needed
            // const apiCurrentPage = response.data.pagination.current_page;
            // const apiPerPage = response.data.pagination.per_page;
          } else {
            this.totalItems = response.data?.total || this.transactions.length;
          }
        } else {
          this.transactions = [];
          this.totalItems = 0;
          
          // Show specific error message if available
          if (response.message) {
            this.$swal({
              title: 'Information',
              text: response.message,
              icon: 'info',
              confirmButtonText: 'OK'
            });
          }
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
        this.transactions = [];
        this.totalItems = 0;
        
        // Show user-friendly error message
        this.$swal({
          title: 'Connection Error',
          text: 'Unable to load transactions. Please check your internet connection and try again.',
          icon: 'error',
          confirmButtonText: 'Retry',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.fetchTransactions();
          }
        });
      } finally {
        this.isLoading = false;
      }
    },
    getHeaderStyle(header) {
      return {
        minWidth: header.width + "px",
        maxWidth: header.width + "px",
      };
    },
    changePage(page) {
      // Convert to number to ensure proper comparison
      page = Number(page);
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchTransactions();
      }
    },
    
    // Handle pagination for filtered data
    onPreviousPageFiltered() {
      if (this.hasPrevPage) {
        // Always use server-side pagination
        this.changePage(this.currentPage - 1);
      }
    },
    
    onNextPageFiltered() {
      if (this.hasNextPage) {
        // Always use server-side pagination
        this.changePage(this.currentPage + 1);
      }
    },
    
    onPerPageChange() {
      // Always use server-side pagination
      this.changePage(1);
    },
    async exportData() {
      // Validasi: export hanya boleh untuk range 1 hari (tanggal mulai dan akhir sama)
      if (
        this.dateRange &&
        Array.isArray(this.dateRange) &&
        this.dateRange.length === 2 &&
        this.dateRange[0] &&
        this.dateRange[1]
      ) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        // Bandingkan hanya berdasarkan tanggal (tanpa jam)
        const isSameDay = startDate.toDateString() === endDate.toDateString();

        if (!isSameDay) {
          this.$swal({
            title: 'Invalid Date Range',
            text: 'Export hanya bisa untuk rentang 1 hari. Silakan pilih tanggal yang sama untuk Start dan End.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
          return;
        }
      }

      try {
        this.isLoading = true;
        this.isExporting = true; // Set flag to hide default loading

        // Show loading alert for export process
        this.$swal({
          title: 'Preparing Export...',
          html: `
            <div style="text-align: center; padding: 20px 0;">
              <p style="margin: 10px 0; color: #495057; font-size: 14px;">
                Sedang memproses data transaksi untuk di-export...
              </p>
              <p style="margin: 10px 0; color: #6c757d; font-size: 12px;">
                Untuk data yang banyak, proses ini mungkin membutuhkan waktu beberapa saat.<br>
                Mohon tunggu dan jangan tutup halaman ini.
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Build params for backend download API
        const params = {};

        // Add status filter if set
        if (this.status && this.status.trim() !== "") {
          params.status = this.status.trim();
        }

        // Add bank filter if set
        if (this.bankId && this.bankId !== "") {
          params.bank_id = this.bankId;
        }

        // Add date range filter if set
        if (
          this.dateRange &&
          Array.isArray(this.dateRange) &&
          this.dateRange.length === 2 &&
          this.dateRange[0] &&
          this.dateRange[1]
        ) {
          params.start_date = this.formatDateToAPI(new Date(this.dateRange[0]));
          params.end_date = this.formatDateToAPI(new Date(this.dateRange[1]));
        }

        // Show loading alert for download
        this.$swal({
          title: 'Downloading...',
          html: `
            <div style="text-align: center; padding: 20px 0;">
              <p style="margin: 10px 0; color: #495057; font-size: 14px;">
                Sedang download, mohon ditunggu...
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Fetch data
        const backendApi = process.env.BACKEND_API || "https://api.swiftrans.id";
        const token = localStorage.getItem("auth_token");
        let exportUrl = `${backendApi}/api/merchant/transactions/download`;
        
        // Build query string
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append('status', params.status);
        if (params.bank_id) queryParams.append('bank_id', params.bank_id);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.transaction_id) queryParams.append('transaction_id', params.transaction_id);
        
        if (queryParams.toString()) {
          exportUrl += `?${queryParams.toString()}`;
        }

        const response = await fetch(exportUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download file');
        }

        // Get blob directly
        const blob = await response.blob();
        
        // Close progress alert
        this.$swal.close();

        if (!blob || blob.size === 0) {
          this.$swal({
            title: 'No Data',
            text: 'No data to export',
            icon: 'info',
            confirmButtonText: 'OK'
          });
          return;
        }

        // Generate informative filename
        const now = new Date();
        const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format
        const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, ""); // HHMMSS format
        
        // Build filename with filter info
        let filterInfo = "";
        if (this.status) filterInfo += `_status-${this.status}`;
        if (this.bankId) {
          const selectedBank = this.bankOptions.find(b => b.id === this.bankId);
          const bankName = selectedBank ? selectedBank.bank_name : this.bankId;
          filterInfo += `_bank-${bankName}`;
        }
        if (this.dateRange && this.dateRange.length === 2) {
          const startDate = new Date(this.dateRange[0]);
          const endDate = new Date(this.dateRange[1]);
          filterInfo += `_from-${startDate.toISOString().split("T")[0]}_to-${endDate.toISOString().split("T")[0]}`;
        }
        
        const filename = `api-transactions${filterInfo}_${dateStr}_${timeStr}.csv`;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$swal({
          title: 'Success!',
          text: 'Transactions exported successfully',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } catch (error) {
        console.error('Error exporting data:', error);
        // Close loading alert if still open
        this.$swal.close();
        this.$swal({
          title: 'Export Failed',
          text: error.message || 'Error exporting data. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isLoading = false;
        this.isExporting = false; // Reset flag to show default loading again
      }
    },
    formatNumber(value) {
      return formatNumberUtil(value);
    },
    maskAccountNumber(accountNumber, bank) {
      return maskAccountNumber(accountNumber, bank);
    },
    formatDateToAPI(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async handleResendCallback(transaction) {
      // Show confirmation dialog
      const result = await this.$swal({
        title: 'Confirm Resend Callback',
        html: `
          <div style="text-align: left; margin: 20px 0;">
            <p style="margin-bottom: 15px;">Are you sure you want to resend the callback for this transaction?</p>
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #007bff;">
              <strong>Transaction ID:</strong><br>
              <span style="font-family: monospace; color: #495057;">${transaction.transaction_id}</span><br><br>
              <strong>Amount:</strong> IDR ${this.formatNumber(transaction.amount)}<br>
              <strong>Status:</strong> <span style="color: ${this.getStatusColor(transaction.status)};">${transaction.status}</span>
            </div>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Resend',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#007bff',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        focusCancel: true
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.isResendingCallback = transaction.transaction_id;
        
        const response = await resendCallback(transaction.transaction_id);
        
        if (response.status === 'success') {
          this.$swal({
            title: 'Success!',
            text: 'Callback has been resent successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
            timer: 3000,
            timerProgressBar: true
          });
        } else {
          this.$swal({
            title: 'Error!',
            text: response.message || 'Failed to resend callback. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      } catch (error) {
        console.error('Error resending callback:', error);
        this.$swal({
          title: 'Error!',
          text: error.message || 'Failed to resend callback. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isResendingCallback = null;
      }
    },
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'success': return '#28a745';
        case 'failed': return '#dc3545';
        case 'pending': return '#fd7e14';
        case 'processing': return '#6c757d';
        default: return '#495057';
      }
    },
    async copyToClipboard(text, label) {
      try {
        await navigator.clipboard.writeText(text);
        this.$swal({
          title: 'Copied!',
          text: `${label} has been copied to clipboard`,
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } catch (error) {
        console.error('Failed to copy:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        this.$swal({
          title: 'Copied!',
          text: `${label} has been copied to clipboard`,
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      }
    },
    openTransactionDetail(transaction) {
      this.selectedTransaction = transaction;
      this.showTransactionModal = true;
    },
    closeTransactionModal() {
      this.showTransactionModal = false;
      this.selectedTransaction = null;
    },
    navigateToBalance(uuid) {
      if (uuid) {
        this.$router.push({
          name: 'Balance',
          query: { ref_id: uuid }
        });
      }
    },
    selectBank(bankId) {
      this.bankId = bankId;
      this.bankSearch = '';
      this.showBankDropdown = false;
    },
    toggleBankDropdown() {
      this.showBankDropdown = !this.showBankDropdown;
      if (this.showBankDropdown) {
        this.$nextTick(() => {
          const input = this.$el.querySelector('.searchable-input');
          if (input) input.focus();
        });
      }
    },
    closeBankDropdown() {
      this.showBankDropdown = false;
      this.bankSearch = '';
    },
  },
  watch: {
    search: {
      handler() {
        // Debounce search to avoid too many API calls
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.currentPage = 1;
          this.fetchTransactions();
        }, 500);
      },
      deep: true,
    },
    status: {
      handler() {
        this.currentPage = 1;
        this.fetchTransactions();
      },
      deep: true,
    },
    bankId: {
      handler() {
        this.currentPage = 1;
        this.fetchTransactions();
      },
      deep: true,
    },
    dateRange: {
      handler() {
        this.currentPage = 1;
        this.fetchTransactions();
      },
      deep: true,
    },
  },
  beforeUnmount() {
    // Clean up timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: scroll;
  width: 100%;
  min-height: 330px;
}

table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1200px;
}

.table-header {
  padding: 13px 8px;
  height: 40px;
  min-width: 100px;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
  background-color: #fcfcfd;
}

.table-body {
  padding: 10px 8px;
  height: 40px;
  min-width: 100px;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
  background-color: #ffffff;
}

.sticky-col-left {
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 4px 0 6px -2px rgba(0, 0, 0, 0.1);
}

.sticky-col-right {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -4px 0 6px -2px rgba(0, 0, 0, 0.08);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.styled-input {
  height: 40px;
  border-radius: 100px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #f8fafc !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 14px !important;
  color: #4a5568;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s ease-in-out;
}

.styled-input:focus {
  border-color: #4299e1 !important;
  outline: none;
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.5);
}

.search-input.styled-input {
  padding-left: 40px !important;
}

.status-select.styled-input {
  padding-left: 12px !important;
  padding-right: 35px !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
}

.input-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.icon-left {
  left: 12px;
}

.icon-right {
  right: 12px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
  text-align: center;
}

.status-badge.success {
  background-color: #dcfce7;
  color: #15803d;
}

.status-badge.failed {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-badge.draft {
  background-color: #f3f4f6;
  color: #4b5563;
}

.status-badge.waiting {
  background-color: #fff7ed;
  color: #ea580c;
}

.status-badge.refunded {
  background-color: #fef3c7;
  color: #d97706;
}

.status-badge.process {
  background-color: #dbeafe;
  color: #1d4ed8;
}

/* Skeleton Loader Styles */
.skeleton-container {
  padding: 0 16px;
  margin-bottom: 16px;
}

.skeleton-input {
  height: 40px;
  border-radius: 8px;
  background-color: #e2e8f0;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-select {
  width: 150px;
}

.skeleton-date {
  width: 200px;
}

.skeleton-button {
  height: 40px;
  border-radius: 8px;
  background-color: #e2e8f0;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-export {
  width: 80px;
}

.skeleton-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1200px;
}

.skeleton-table-header {
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 8px;
  padding: 13px 8px;
  height: 40px;
  background-color: #fcfcfd;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
}

.skeleton-cell-header {
  height: 100%;
  background-color: #e2e8f0;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-table-row {
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 8px;
  padding: 10px 8px;
  height: 40px;
  background-color: #ffffff;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
}

.skeleton-cell {
  height: 100%;
  background-color: #e2e8f0;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    background-color: #e2e8f0;
  }
  50% {
    background-color: #f0f4f8;
  }
  100% {
    background-color: #e2e8f0;
  }
}

/* Table Optimizations */
.table-hover tbody tr:hover {
  background-color: #f8f9fa;
  transition: background-color 0.15s ease-in-out;
}

.table-row-hover {
  transition: all 0.15s ease-in-out;
}

.row-processing {
  background-color: #fff3cd !important;
  opacity: 0.8;
}

.row-processing td {
  position: relative;
}

.table-header-sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #fcfcfd;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.resend-btn-custom {
  /* Button base styling */
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  
  /* Flexbox for centering icon */
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Transitions */
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Remove default button styles */
  outline: none;
  font-family: inherit;
  font-size: 14px;
}

.resend-btn-custom:hover:not(:disabled) {
  border-color: #3b82f6;
  background: #f8fafc;
  color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.resend-btn-custom:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
}

.resend-btn-custom:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.resend-btn-custom.loading {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #3b82f6;
}

.resend-btn-custom i {
  font-size: 12px;
  line-height: 1;
}

/* Success state for button after successful resend */
.resend-btn-custom.success {
  border-color: #10b981;
  background: #ecfdf5;
  color: #10b981;
}

.resend-btn-custom.success:hover {
  border-color: #059669;
  background: #d1fae5;
  color: #059669;
}

/* Focus styles for accessibility */
.resend-btn-custom:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Pulse animation for loading state */
.resend-btn-custom.loading {
  animation: pulse-border 2s infinite;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: #3b82f6;
  }
  50% {
    border-color: #60a5fa;
  }
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-state-content i {
  opacity: 0.5;
}

/* Responsive Improvements */
@media (max-width: 768px) {
  .table-wrapper {
    border-radius: 8px;
    border: 1px solid #dee2e6;
  }
  
  .sticky-col-left,
  .sticky-col-right {
    box-shadow: none;
  }
  
  .resend-btn-custom {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
  
  .resend-btn-custom i {
    font-size: 11px;
  }

  .searchable-select-wrapper {
    min-width: 160px;
  }
  
  .searchable-dropdown {
    max-height: 200px;
  }
}

/* Bank Name Cell */
.bank-name-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  cursor: help;
}

/* Transaction ID Cell */
.transaction-id-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  position: relative;
}

.transaction-id-text {
  color: #007bff;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.transaction-id-text.clickable {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.transaction-id-text.clickable:hover {
  color: #0056b3;
  text-decoration: underline;
}

.transaction-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: all 0.2s ease-in-out;
}

.transaction-id-cell:hover .transaction-actions {
  opacity: 1;
}

.copy-icon, .view-icon {
  color: #6c757d;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease-in-out;
  flex-shrink: 0;
  padding: 2px;
  border-radius: 3px;
}

.copy-icon:hover, .view-icon:hover {
  color: #007bff;
  background-color: #f8f9fa;
  transform: scale(1.1);
}

.refund-badge-small {
  background-color: #dc3545;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: absolute;
  top: -8px;
  right: -8px;
  animation: pulse 2s infinite;
  z-index: 1;
}

.uuid-link {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  gap: 4px;
}

.uuid-link:hover {
  color: #0056b3;
  background-color: #f8f9fa;
  padding: 2px 4px;
  border-radius: 3px;
}

/* Status Badge Improvements */
.status-badge {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

/* Loading State for Table */
.table tbody tr.row-processing::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 1.5s infinite;
  pointer-events: none;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Searchable Select Dropdown */
.searchable-select-wrapper {
  position: relative;
  min-width: 200px;
}

.searchable-input {
  cursor: pointer;
}

.searchable-input::placeholder {
  color: #4a5568;
  opacity: 1;
}

.searchable-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 280px;
  overflow-y: auto;
  z-index: 1000;
  animation: dropdownFadeIn 0.2s ease-in-out;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  font-size: 14px;
  color: #4a5568;
  border-bottom: 1px solid #f3f4f6;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #f8fafc;
  color: #2563eb;
}

.dropdown-item.selected {
  background-color: #eff6ff;
  color: #2563eb;
  font-weight: 500;
}

.dropdown-item.selected::after {
  content: '✓';
  float: right;
  color: #2563eb;
  font-weight: bold;
}

.dropdown-item.no-results {
  color: #9ca3af;
  text-align: center;
  cursor: default;
  font-style: italic;
}

.dropdown-item.no-results:hover {
  background-color: transparent;
  color: #9ca3af;
}

/* Scrollbar styling for dropdown */
.searchable-dropdown::-webkit-scrollbar {
  width: 6px;
}

.searchable-dropdown::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 0 8px 8px 0;
}

.searchable-dropdown::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.searchable-dropdown::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Pagination Improvements */
.pagination-controls {
  background: linear-gradient(to right, #f8f9fa, #ffffff, #f8f9fa);
  border-radius: 0 0 8px 8px;
}

.pagination .page-link {
  border: none;
  color: #495057;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
}

.pagination .page-link:hover {
  background-color: #e9ecef;
  transform: translateY(-1px);
}

.pagination .page-item.disabled .page-link {
  opacity: 0.5;
  cursor: not-allowed;
}
</style> 